/**
 * Auth Store - Reactive state management for authentication
 * @description Manages user authentication state and role-based access
 */
import Vue from "vue";

/**
 * User roles enum
 */
export const UserRoles = {
  INVESTOR: "investor",
  ENTREPRENEUR: "entrepreneur",
};

/**
 * Reactive auth state
 */
const state = Vue.observable({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});

/**
 * Auth Store with getters, actions and mutations
 */
const authStore = {
  // ============================================
  // Getters
  // ============================================

  /**
   * Returns the current state
   * @returns {Object}
   */
  get state() {
    return state;
  },

  /**
   * Returns the current user
   * @returns {Object|null}
   */
  get user() {
    return state.user;
  },

  /**
   * Returns authentication status
   * @returns {Boolean}
   */
  get isAuthenticated() {
    return state.isAuthenticated;
  },

  /**
   * Returns loading status
   * @returns {Boolean}
   */
  get isLoading() {
    return state.isLoading;
  },

  /**
   * Returns the user's role
   * @returns {String|null}
   */
  get role() {
    return state.user?.role || null;
  },

  /**
   * Checks if user is an investor
   * @returns {Boolean}
   */
  get isInvestor() {
    return state.user?.role === UserRoles.INVESTOR;
  },

  /**
   * Checks if user is an entrepreneur
   * @returns {Boolean}
   */
  get isEntrepreneur() {
    return state.user?.role === UserRoles.ENTREPRENEUR;
  },

  // ============================================
  // Actions
  // ============================================

  /**
   * Initializes auth state from localStorage
   * @returns {Promise<void>}
   */
  async init() {
    state.isLoading = true;
    try {
      const token = localStorage.getItem("auth_token");
      const userData = localStorage.getItem("auth_user");

      if (token && userData) {
        state.user = JSON.parse(userData);
        state.isAuthenticated = true;
      } else if (process.env.NODE_ENV === "development") {
        const devUser = { id: 1, email: "dev@kemaris.de", name: "Dev User", role: UserRoles.ENTREPRENEUR, avatar: "/img/default-avatar.png" };
        localStorage.setItem("auth_token", "dev-token");
        localStorage.setItem("auth_user", JSON.stringify(devUser));
        state.user = devUser;
        state.isAuthenticated = true;
      }
    } catch (error) {
      console.error("[AuthStore] Init error:", error);
      this.logout();
    } finally {
      state.isLoading = false;
    }
  },

  /**
   * Logs in a user and stores credentials
   * @param {Object} credentials - Login credentials
   * @param {String} credentials.email - User email
   * @param {String} credentials.password - User password
   * @param {String} credentials.role - User role (investor/entrepreneur)
   * @returns {Promise<Object>}
   */
  async login({ email, password, role = UserRoles.INVESTOR }) {
    state.isLoading = true;

    try {
      // Mock login - replace with actual API call
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (!email || password.length < 6) {
        throw new Error("Ungueltige Anmeldedaten");
      }

      const user = {
        id: Date.now(),
        email,
        name: this._getNameFromEmail(email),
        role,
        avatar: "/img/default-avatar.png",
      };

      const token = "mock-jwt-token-" + Date.now();

      // Persist to localStorage
      localStorage.setItem("auth_token", token);
      localStorage.setItem("auth_user", JSON.stringify(user));

      // Update state
      state.user = user;
      state.isAuthenticated = true;

      return { user, token };
    } finally {
      state.isLoading = false;
    }
  },

  /**
   * Logs out the current user
   */
  logout() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    state.user = null;
    state.isAuthenticated = false;
  },

  /**
   * Updates the current user's data
   * @param {Object} userData - Partial user data to update
   */
  updateUser(userData) {
    if (state.user) {
      state.user = { ...state.user, ...userData };
      localStorage.setItem("auth_user", JSON.stringify(state.user));
    }
  },

  /**
   * Switches the user's role (for demo purposes)
   * @param {String} newRole - New role to switch to
   */
  switchRole(newRole) {
    if (Object.values(UserRoles).includes(newRole)) {
      this.updateUser({ role: newRole });
    }
  },

  /**
   * Checks if user has access to a specific role
   * @param {String|Array} allowedRoles - Role(s) that have access
   * @returns {Boolean}
   */
  hasAccess(allowedRoles) {
    if (!state.user) return false;

    const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
    return roles.includes(state.user.role);
  },

  // ============================================
  // Private Helpers
  // ============================================

  /**
   * Extracts name from email for mock data
   * @private
   * @param {String} email
   * @returns {String}
   */
  _getNameFromEmail(email) {
    const namePart = email.split("@")[0];
    return namePart
      .split(/[._-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },
};

export default authStore;
